/**
 * Ranking do periodo: as cinco maiores por margem e por recuperado bruto.
 *
 * Decisoes nao obvias:
 *  1. Duas listas, nao um anel: com centenas de empresas o anel vira
 *     confete; cinco linhas com numero, participacao e variacao se leem em
 *     um segundo. "Ver todas na tabela" leva ao resto, ja ordenado.
 *  2. Por MARGEM entram as empresas ativas com comissao ou custo no
 *     periodo (uma margem negativa e informacao, nao ruido). Por RECUPERADO
 *     entram as que recuperaram algo.
 *  3. A variacao e a compacta (glifo + percentual) com o texto completo no
 *     `title`, para a linha caber.
 *  4. A nota de concentracao (quanto as tres maiores respondem da comissao)
 *     e o risco que o ranking mostra sem dizer: dependencia de poucas.
 */

import { Button, CompanyLogo, Footnote, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, VALOR_AUSENTE } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { participacaoBps } from '../comparar';
import type { ColunaDaTabela } from '../tabela';
import type { CompanyRow } from '../types';
import { Delta } from './Delta';
import { identidadeDaEmpresa } from './IdentidadeEmpresa';

const TAMANHO_DO_TOPO = 3;
const LIMITE = 5;

interface Linha {
  readonly row: CompanyRow;
  readonly valorCents: number;
  readonly anteriorCents: number | null;
  readonly secundario: string;
}

function Lista({
  titulo,
  linhas,
  vazio,
  companies,
  onVerTabela,
}: {
  readonly titulo: string;
  readonly linhas: readonly Linha[];
  readonly vazio: string;
  readonly companies: readonly CompanySummary[];
  readonly onVerTabela: () => void;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">{titulo}</h3>
      {linhas.length === 0 ? (
        <p className="rounded-xl border border-dashed border-edge bg-elevated px-3 py-4 text-center text-xs text-ink-subtle">{vazio}</p>
      ) : (
        <ol className="flex flex-col divide-y divide-line/60">
          {linhas.map((l, i) => {
            const identidade = identidadeDaEmpresa(l.row.companyId, companies, l.row.companyName);
            return (
              <li key={l.row.companyId} className="flex items-center gap-3 py-2">
                <span className="w-4 shrink-0 text-right text-xs tabular-nums text-ink-subtle" aria-label={`${i + 1}º lugar`}>
                  {i + 1}
                </span>
                <CompanyLogo company={identidade} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{identidade.name}</p>
                  <p className="truncate text-xs text-ink-subtle">{l.secundario}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end">
                  <span className={`text-sm font-semibold tabular-nums ${l.valorCents < 0 ? 'text-status-critical' : 'text-ink'}`}>{formatBRLFromCents(l.valorCents)}</span>
                  <Delta atual={l.valorCents} anterior={l.anteriorCents} tipo="dinheiro" compacto />
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <Button variant="ghost" size="sm" iconRight="seta-dir" onClick={onVerTabela} className="self-start">
        Ver todas na tabela
      </Button>
    </div>
  );
}

export function RankingDeEmpresas({
  rows,
  companies,
  totalComissaoCents,
  onVerTabela,
  className = '',
}: {
  readonly rows: readonly CompanyRow[];
  readonly companies: readonly CompanySummary[];
  readonly totalComissaoCents: number;
  readonly onVerTabela: (coluna: ColunaDaTabela) => void;
  readonly className?: string;
}): JSX.Element {
  // Sem custo medido (motor real) nao ha margem para ranquear: o primeiro
  // ranking vira "por comissao", que e o que existe de fato.
  const semCusto = rows.some((r) => r.marginCents === null || r.costCents === null);
  const porMargem: readonly Linha[] = semCusto
    ? rows
        .filter((r) => r.commissionCents > 0)
        .sort((a, b) => b.commissionCents - a.commissionCents)
        .slice(0, LIMITE)
        .map((r) => ({
          row: r,
          valorCents: r.commissionCents,
          anteriorCents: r.previous === null ? null : r.previous.commissionCents,
          secundario: `${formatPercentFromBps(r.feeBps)} sobre ${formatBRLFromCents(r.recoveredAmountCents)} recuperados · custo não medido`,
        }))
    : rows
        .filter((r) => r.status === 'ativa' && (r.commissionCents > 0 || (r.costCents ?? 0) > 0))
        .sort((a, b) => (b.marginCents ?? 0) - (a.marginCents ?? 0))
        .slice(0, LIMITE)
        .map((r) => ({
          row: r,
          valorCents: r.marginCents ?? 0,
          anteriorCents: r.previous === null ? null : r.previous.marginCents,
          secundario:
            r.marginBps === null
              ? `sem comissão · ${formatBRLFromCents(r.costCents ?? 0)} de custo`
              : `${formatPercentFromBps(r.marginBps)} da comissão · custo ${formatBRLFromCents(r.costCents ?? 0)}`,
        }));

  const porRecuperado: readonly Linha[] = rows
    .filter((r) => r.recoveredAmountCents > 0)
    .sort((a, b) => b.recoveredAmountCents - a.recoveredAmountCents)
    .slice(0, LIMITE)
    .map((r) => ({
      row: r,
      valorCents: r.recoveredAmountCents,
      anteriorCents: r.previous === null ? null : r.previous.recoveredAmountCents,
      secundario: `${formatCount(r.recoveredCount)} ${r.recoveredCount === 1 ? 'venda' : 'vendas'} · taxa ${r.abandonedCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(r.recoveryRateBps)} · comissão ${formatBRLFromCents(
        r.commissionCents,
      )}`,
    }));

  const topo = [...rows].sort((a, b) => b.commissionCents - a.commissionCents).slice(0, TAMANHO_DO_TOPO);
  const somaTopo = topo.reduce((acc, r) => acc + r.commissionCents, 0);
  const concentracao = participacaoBps(somaTopo, totalComissaoCents);

  return (
    <SectionCard
      id="ranking"
      title="Ranking do período"
      icon="estrela"
      description={
        semCusto
          ? 'As cinco maiores por comissão e por recuperado bruto, com a variação contra o período anterior. Margem entra quando o custo for medido.'
          : 'As cinco maiores por margem e por recuperado bruto, com a variação contra o período anterior.'
      }
      className={className}
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-6 md:grid-cols-2">
          <Lista
            titulo={semCusto ? 'Top 5 por comissão' : 'Top 5 por margem'}
            linhas={porMargem}
            vazio={semCusto ? 'Nenhuma comissão no período.' : 'Nenhuma empresa ativa com comissão ou custo no período.'}
            companies={companies}
            onVerTabela={() => onVerTabela(semCusto ? 'comissao' : 'margem')}
          />
          <Lista titulo="Top 5 por recuperado" linhas={porRecuperado} vazio="Nenhuma venda recuperada no período." companies={companies} onVerTabela={() => onVerTabela('recuperado')} />
        </div>
        <Footnote>
          {concentracao === null || topo.length === 0
            ? 'Sem comissão no período, não há concentração para medir.'
            : `As ${formatCount(topo.length)} maiores respondem por ${formatPercentFromBps(concentracao)} da comissão do período. Quanto mais concentrado, mais uma pausa de uma empresa pesa no faturamento.`}
        </Footnote>
      </div>
    </SectionCard>
  );
}
